import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
import { useMemo, useState } from 'react';

import { Button } from '@/components/core/Button/Button';
import { Callout } from '@/components/core/Callout';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogHeaderDefault } from '@/components/core/Dialog';
import { Loader } from '@/components/core/Loader';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetLabel } from '@/pages/dam/api';
import type { LabelledAsset } from '@/pages/dam/api/damLabels';
import { filterLabelsByName, findLabelByName, sortLabelsAlphabetically } from '@/pages/dam/utils/damTags';

import { DamTagAssignmentRow } from '../DamTagAssignmentRow';
import { DamTagNameField } from '../DamTagNameField';
import { DamTagSortMenu } from '../DamTagSortMenu';

type DamTagTarget = 'all' | 'none';

interface DamManageTagsDialogProps {
    /** Number of selected assets, shown while their tags are loading. */
    count: number;
    /** Tags of the selected assets; `undefined` while loading. */
    assets?: LabelledAsset[];
    labels: AssetLabel[];
    /** Creates a tag and resolves it, or `undefined` when that failed. */
    onCreateLabel: (name: string) => Promise<AssetLabel | undefined>;
    onConfirm: (added: string[], removed: string[]) => void;
    /** Changes saved so far while the dialog saves; `undefined` before confirmation. */
    progress?: { done: number; count: number };
    onClose: () => void;
}

/**
 * Bulk tag dialog "Manage tags for N assets" (wireframe `7170:210177`, create `7170:210409`,
 * typing `7184:242651`): "X of Y tags assigned" with sorting, "Create tag", and per tag
 * "(k of n assigned)" with "Assign all". Changes are a preview until Confirm; Cancel drops
 * them. A tag created here is marked as assigned to all selected assets (D-038).
 */
function DamManageTagsDialog({ count, assets, labels, onCreateLabel, onConfirm, progress, onClose }: DamManageTagsDialogProps) {
    const { t } = useTranslation();
    const [targets, setTargets] = useState<Record<string, DamTagTarget>>({});
    const [query, setQuery] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const isLoading = assets === undefined;
    const isSaving = progress !== undefined;
    const total = assets?.length ?? count;

    const originalCounts = useMemo(() => {
        const counts = new Map<string, number>();
        assets?.forEach(({ labelIds }) => labelIds.forEach((id) => counts.set(id, (counts.get(id) ?? 0) + 1)));
        return counts;
    }, [assets]);

    const assignedCount = (labelId: string) => {
        const target = targets[labelId];
        if (target === 'all') {
            return total;
        }
        return target === 'none' ? 0 : originalCounts.get(labelId) ?? 0;
    };

    const sortedLabels = useMemo(() => sortLabelsAlphabetically(labels), [labels]);
    const visibleLabels = filterLabelsByName(sortedLabels, query ?? '');
    const assignedTagCount = labels.filter((label) => assignedCount(label.id) > 0).length;
    const typedName = query?.trim() ?? '';
    const canCreate = typedName !== '' && !findLabelByName(labels, typedName);

    const setTarget = (labelId: string, target: DamTagTarget) => setTargets((current) => ({ ...current, [labelId]: target }));

    const create = async () => {
        if (query === null) {
            setQuery('');
            return;
        }
        if (!canCreate || isCreating) {
            return;
        }
        setIsCreating(true);
        const label = await onCreateLabel(typedName);
        setIsCreating(false);
        if (label) {
            setTarget(label.id, 'all');
            setQuery(null);
        }
    };

    const changedIds = Object.keys(targets).filter((id) => {
        const original = originalCounts.get(id) ?? 0;
        return targets[id] === 'all' ? original < total : original > 0;
    });
    const added = changedIds.filter((id) => targets[id] === 'all');
    const removed = changedIds.filter((id) => targets[id] === 'none');

    return (
        <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault closeLabel={t('CLOSE')}>
                        {total === 1 ? t('DAM.TAGS.MANAGE_TITLE_ONE') : t('DAM.TAGS.MANAGE_TITLE', { count: total.toLocaleString() })}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-flex-col tw-gap-4">
                    {isLoading ? (
                        <div className="tw-flex tw-justify-center tw-py-4">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <span className="tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">
                                    {t('DAM.TAGS.ASSIGNED_SUMMARY', { count: assignedTagCount.toLocaleString(), total: labels.length.toLocaleString() })}
                                </span>
                                <DamTagSortMenu />
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-2">
                                {query !== null && (
                                    <DamTagNameField
                                        value={query}
                                        onChange={setQuery}
                                        onSubmit={() => void create()}
                                        onCancel={() => setQuery(null)}
                                        disabled={isCreating || isSaving}
                                        autoFocus
                                    />
                                )}
                                <Button
                                    variant="outline"
                                    hasIcon
                                    icon={PlusIcon}
                                    loading={isCreating}
                                    disabled={isCreating || isSaving || (query !== null && !canCreate)}
                                    onClick={() => void create()}
                                    className="tw-w-full"
                                >
                                    {canCreate ? t('DAM.TAGS.CREATE_NAMED', { name: typedName }) : t('DAM.TAGS.CREATE')}
                                </Button>
                            </div>
                            {visibleLabels.length > 0 ? (
                                <ul className="tw-m-0 tw-max-h-80 tw-list-none tw-overflow-y-auto tw-p-0">
                                    {visibleLabels.map((label) => (
                                        <li key={label.id}>
                                            <DamTagAssignmentRow
                                                name={label.name}
                                                assignedCount={assignedCount(label.id)}
                                                total={total}
                                                onToggle={() => setTarget(label.id, assignedCount(label.id) === total ? 'none' : 'all')}
                                                onAssignAll={() => setTarget(label.id, 'all')}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                labels.length === 0 && (
                                    <p className="tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{t('DAM.LIBRARY.NO_TAGS')}</p>
                                )
                            )}
                            {isSaving && (
                                <Callout variant="info" canBeClosed={false}>
                                    <span className="tw-text-t-sm lg:tw-text-t-base" role="status">
                                        {t('DAM.TAGS.SAVE_PROGRESS', { done: progress.done.toLocaleString(), count: progress.count.toLocaleString() })}
                                    </span>
                                </Callout>
                            )}
                        </>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" disabled={isSaving} onClick={onClose}>{t('CANCEL')}</Button>
                    <Button
                        loading={isSaving}
                        disabled={isLoading || isSaving || changedIds.length === 0}
                        onClick={() => onConfirm(added, removed)}
                    >
                        {t('DAM.TAGS.CONFIRM')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { DamManageTagsDialog };
export type { DamManageTagsDialogProps };
